/** Data layer for Nagpur's "Alerts per Trip" panel -- GET /api/bus/trip-alerts(+/replay),
 * serving/app.py, backed by db/reports_nagpur_trip_alerts.sql's v_nagpur_trip_alerts.
 *
 * Never calls the EDMS schedule host directly and never will: CORS on that host allows
 * only the console's own origin, and the depot token is a server secret (SPEC.md's
 * Non-goals). This file only ever talks to our own /api/bus/* origin.
 */

export interface NagpurTripAlertRow {
  trip_id: string
  route: string | null
  origin_stage: string | null
  destination_stage: string | null
  vehicle_id: string
  driver_name: string | null
  schedule_code: string | null
  duty_day: string | null
  /** Epoch ms -- null only when the stored clock text failed to parse. */
  started_ts: number | null
  ended_ts: number | null
  started: boolean
  completed: boolean
  harsh_brake: number
  harsh_accel: number
  total_alerts: number
}

export interface NagpurTripAlertsPage {
  rows: NagpurTripAlertRow[]
  count: number
  total: number
}

export interface FetchNagpurTripAlertsOpts {
  dateFrom?: string
  dateTo?: string
  vehicleId?: string
  limit?: number
  offset?: number
}

export async function fetchNagpurTripAlerts(
  opts: FetchNagpurTripAlertsOpts = {},
): Promise<NagpurTripAlertsPage> {
  const params = new URLSearchParams()
  if (opts.dateFrom) params.set('date_from', opts.dateFrom)
  if (opts.dateTo) params.set('date_to', opts.dateTo)
  if (opts.vehicleId) params.set('vehicle_id', opts.vehicleId)
  params.set('limit', String(opts.limit ?? 50))
  params.set('offset', String(opts.offset ?? 0))

  const res = await fetch(`/api/bus/trip-alerts?${params.toString()}`)
  if (!res.ok) throw new Error(`failed to fetch trip alerts (${res.status})`)
  return res.json()
}

export interface NagpurTripAlertReplayPoint {
  ts: number
  lat: number | null
  lon: number | null
  speed_kph: number | null
  heading_deg: number | null
}

export interface NagpurTripAlertReplayMarker {
  alert_id: string
  kind: string
  ts: number
  lat: number | null
  lon: number | null
}

export interface NagpurTripAlertReplay {
  trip_id: string
  vehicle_id: string
  points: NagpurTripAlertReplayPoint[]
  alerts: NagpurTripAlertReplayMarker[]
}

export async function fetchNagpurTripAlertReplay(
  tripId: string,
): Promise<NagpurTripAlertReplay> {
  const res = await fetch(`/api/bus/trip-alerts/${encodeURIComponent(tripId)}/replay`)
  if (!res.ok) throw new Error(`failed to fetch trip replay (${res.status})`)
  return res.json()
}
